import React, { useCallback, useMemo, useState } from 'react';
import {
  Container,
  BoxModel,
  Header,
  Title,
  BlockContent,
  MainContent,
  Div,
  ShowTask,
  ShowHide,
  TaskContainer
} from './styles';
import { useHistory } from 'react-router-dom';
import { StatusListInterface } from '../../../../interfaces/ProjectInterface';
import { isEmpty } from '../../../../helpers/common';
import DeleteModal from '../../../../component/modals/deleteModal';
import { useHotkeys } from 'react-hotkeys-hook';
import { COMMENT_TYPE, SHOW_TASK_BY_ORDER, SHOW_TASK_GROUPING_TYPE, TASK_PRIORITY } from '../../../../global/constants';
import Modal from '../../../../component/modals/modal';
import { createNewComment, updateTaskDetails } from '../../../../services/taskServices';
import Taskgridloading from '../../../../component/loading/taskGridLoading';
import { trackAnalyticActivity } from '../../../../services/analyticsService';
import { useTaskActions } from '../../../../hooks/useTaskActions';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  useDroppable,
  DragStartEvent,
  DragOverEvent,
  DragEndEvent
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { MobxFilteredTasksListInterface } from '../../../../interfaces/TaskInterface';
import { captureException } from '../../../../services/logService';
import Calendarviewcard from '../../../../component/calendarViewCard';
import { TASK_ANALYTICS } from '../../../../global/analyticsConstants';
import { rootStore } from '../../../../mobx/rootStore';
import MyCustomContextMenuV2 from '../../../../component/customContextMenu';
import { TaskModel } from '../../../../mobx/models/task';
import { Milestone } from '../../../../mobx/interfaces/project';
import { observer } from 'mobx-react-lite';
import CollapseDropdown from '../../../../component/dropdowns/collapseDropdown';
import CreateTaskModalV2 from '../../../../component/modals/createTaskModal';
import { defaultDisplayFilters } from '../../../../mobx/common';
import { ROUTES } from '../../../../routes/urls';
import { Virtuoso } from 'react-virtuoso';

const DEFAULT_TASK_STATE = {
  filteredTasks: [],
  otherTasks: { tasks: [], completedTasks: [] }
};

const EMPTY_STATUS_LIST: StatusListInterface[] = [];

const POINTER_SENSOR_OPTIONS = { activationConstraint: { distance: 5 } };

interface SortableTaskItemProps {
  task: TaskModel;
  children: React.ReactNode;
}

const SortableTaskItem: React.FC<SortableTaskItemProps> = ({ task, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: task.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1
  };
  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </div>
  );
};

interface DroppableGroupProps {
  item: MobxFilteredTasksListInterface;
  comparedValue: string;
  isCollapsed: boolean;
  groupingStatus?: number;
  disableDragAndDrop: boolean;
  renderTaskCardContent: (task: TaskModel) => React.ReactNode;
  renderCompletedTaskData: (item: MobxFilteredTasksListInterface, comparedValue: string) => React.ReactNode;
  loadData: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  workspace: any;
  collapseIds: string[];
  handleCollapseClick: (id: string) => void;
}

const DroppableGroup: React.FC<DroppableGroupProps> = ({
  item,
  comparedValue,
  isCollapsed,
  groupingStatus,
  disableDragAndDrop,
  renderTaskCardContent,
  renderCompletedTaskData,
  loadData,
  workspace,
  collapseIds,
  handleCollapseClick
}) => {
  const tasks = useMemo(() => (isCollapsed ? [] : item?.tasks || []), [isCollapsed, item?.tasks]);
  const taskIds = useMemo(() => tasks.map((t) => t.id), [tasks]);
  const groupId = String(item.id);
  const { setNodeRef } = useDroppable({ id: groupId });
  const itemContent = useCallback(
    (_index: number, task: TaskModel) => {
      if (disableDragAndDrop) {
        return <TaskContainer>{renderTaskCardContent(task)}</TaskContainer>;
      }

      return (
        <TaskContainer>
          <SortableTaskItem task={task}>{renderTaskCardContent(task)}</SortableTaskItem>
        </TaskContainer>
      );
    },
    [disableDragAndDrop, renderTaskCardContent]
  );

  const FooterComponent = useCallback(
    () => <>{renderCompletedTaskData(item, comparedValue)}</>,
    [renderCompletedTaskData, item, comparedValue]
  );

  const scrollerRef = useCallback(
    (ref: HTMLElement | Window | null) => {
      if (ref instanceof HTMLElement) {
        setNodeRef(ref);
        ref.style.setProperty('scrollbar-width', 'none');
        ref.style.setProperty('-ms-overflow-style', 'none');
      }
    },
    [setNodeRef]
  );

  const virtuosoComponents = useMemo(() => ({ Footer: FooterComponent }), [FooterComponent]);

  return (
    <BoxModel key={item?.id}>
      {!isEmpty(item?.name) && (
        <Header isHide={groupingStatus === SHOW_TASK_GROUPING_TYPE.NO_GROUPING}>
          <Title>{item?.name}</Title>
          <CollapseDropdown
            groupDetail={item}
            loadData={loadData}
            workspaceDetail={workspace}
            collapseIds={collapseIds}
            setCollapse={handleCollapseClick}
          />
        </Header>
      )}
      <SortableContext id={groupId} items={taskIds} strategy={verticalListSortingStrategy}>
        <Virtuoso
          scrollerRef={scrollerRef}
          className='desktop-div-grid-view virtuoso-css'
          data={tasks}
          overscan={300}
          components={virtuosoComponents}
          itemContent={itemContent}
        />
      </SortableContext>
    </BoxModel>
  );
};

interface CompletedTasksToggleProps {
  comparedValue: string;
  selectedId: string;
  showCompletedTask: boolean;
  toggleCompletedTask: (value: boolean, id: string) => void;
  children: React.ReactNode;
}

const CompletedTasksToggle: React.FC<CompletedTasksToggleProps> = React.memo(
  ({ comparedValue, selectedId, showCompletedTask, toggleCompletedTask, children }) => {
    const handleHideCompleted = useCallback(() => toggleCompletedTask(false, ''), [toggleCompletedTask]);

    const handleShowCompleted = useCallback(
      () => toggleCompletedTask(true, comparedValue),
      [toggleCompletedTask, comparedValue]
    );

    return (
      <ShowTask>
        {selectedId === comparedValue && showCompletedTask ? (
          <ShowHide role='button' onClick={handleHideCompleted}>
            Hide Completed Tasks
          </ShowHide>
        ) : (
          <ShowHide className='update-padding' role='button' onClick={handleShowCompleted}>
            Show Completed Tasks
          </ShowHide>
        )}
        {children}
      </ShowTask>
    );
  }
);
CompletedTasksToggle.displayName = 'CompletedTasksToggle';

interface Props {
  loadData: () => void;
  propsLoading?: boolean;
  isView?: boolean;
  disableDragAndDrop?: boolean;
}

const TaskGrid: React.FC<Props> = (props) => {
  const { loadData, propsLoading, isView, disableDragAndDrop = false } = props;
  // States
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<TaskModel>();
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showCompletedTask, setShowCompletedTask] = useState(false);
  const [selectedId, setSelectedId] = useState('');
  const [collapseIds, setCollapseIds] = useState<string[]>([]);
  const loading = propsLoading;
  const { onChangeStatus, onClickTaskCheckBox, confirmationModal } = useTaskActions(loadData);

  //Mobx store variables
  const { projectStore, viewStore, workspaceStore } = rootStore;
  const { currentProject } = projectStore;
  const { taskFilterType: projectTaskFilter, filteredProjectTasks } = currentProject || {};
  const { groupingStatus } = projectTaskFilter || {};
  const { currentView, createViewInput } = viewStore;
  const { currentWorkspace: workspace } = workspaceStore;
  const { filteredTasks: filteredTasksList, otherTasks: otherTask } = isView
    ? viewStore.viewTasks
    : filteredProjectTasks || DEFAULT_TASK_STATE;

  // Other variables
  const taskFilter = isView
    ? createViewInput?.id
      ? createViewInput?.displayFilters || defaultDisplayFilters
      : currentView?.displayFilters
    : projectTaskFilter;
  const history = useHistory();

  //toggle completed task
  const toggleCompletedTask = useCallback((value: boolean, id: string) => {
    if (!value) {
      setSelectedId('');
    }
    setSelectedId(id);
    setShowCompletedTask(value);
  }, []);

  //open delete modal
  const OpenDeleteModal = useCallback((item: TaskModel) => {
    setSelectedItem(item);
    setIsModalOpen(true);
  }, []);

  //close delete modal
  const closeDeleteModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  //handle edit task
  const onClickEdit = useCallback(async (task: TaskModel) => {
    rootStore.taskStore.createTaskInput.updateWith(task);
    setOpen(true);
  }, []);

  //handle clone task
  const onClickClone = useCallback(async (task: TaskModel) => {
    rootStore.taskStore.createTaskInput.updateWith(task);
    rootStore.taskStore.createTaskInput.setDueDate(null);
    rootStore.taskStore.createTaskInput.setId(undefined);
    // Set cloneTaskId if the task has subtasks
    if (task.subTasks && task.subTasks.length > 0) {
      rootStore.taskStore.createTaskInput.setCloneTaskId(task.id);
    }
    setOpen(true);
  }, []);

  //create task close
  const close = useCallback(() => {
    setOpen(false);
    rootStore.taskStore.createTaskInput.clear();
  }, []);

  //handle click task details
  const onClickTaskDetails = useCallback(
    (task: TaskModel) => {
      const eventProperties = {
        source: 'task list',
        title: task?.name || ''
      };
      trackAnalyticActivity(TASK_ANALYTICS.VIEW, eventProperties);
      rootStore.taskStore.setCurrentTask(task?.id);
      history.push(ROUTES.TASK_DETAILS.replace(':id', task?.projectId || '').replace(':taskId', task?.id || ''));
    },
    [history]
  );

  const [activeTaskId, setActiveTaskId] = useState<string | null>(null);
  const [sourceContainerId, setSourceContainerId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, POINTER_SENSOR_OPTIONS),
    useSensor(KeyboardSensor)
  );

  const findContainer = useCallback(
    (id: string): string | undefined => {
      if (filteredTasksList?.find((group) => String(group.id) === id)) {
        return id;
      }
      const group = filteredTasksList?.find((group) => group.tasks?.some((t) => t.id === id));
      return group?.id != null ? String(group.id) : undefined;
    },
    [filteredTasksList]
  );

  const activeTask = useMemo(() => {
    if (!activeTaskId) return null;
    return currentProject?.tasks?.find((t) => t.id === activeTaskId) || null;
  }, [activeTaskId, currentProject?.tasks]);

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const activeId = String(event.active.id);
      setActiveTaskId(activeId);
      const container = findContainer(activeId);
      setSourceContainerId(container || null);
    },
    [findContainer]
  );

  const handleDragOver = useCallback(
    (event: DragOverEvent) => {
      const { active, over } = event;
      if (!over || !filteredTasksList) return;

      const activeId = String(active.id);
      const overId = String(over.id);

      const activeContainer = findContainer(activeId);
      const overContainer = findContainer(overId);

      if (!activeContainer || !overContainer || activeContainer === overContainer) return;

      const activeGroup = filteredTasksList.find((g) => String(g.id) === activeContainer);
      const overGroup = filteredTasksList.find((g) => String(g.id) === overContainer);
      if (!activeGroup || !overGroup) return;

      const draggedTask = currentProject?.tasks?.find((t) => t.id === activeId);
      if (!draggedTask) return;

      const overItems = overGroup.tasks || [];
      const overIndex = overItems.findIndex((t) => t.id === overId);
      const newIndex = overId === overContainer ? overItems.length : overIndex >= 0 ? overIndex : overItems.length;

      activeGroup.tasks = (activeGroup.tasks || []).filter((t) => t.id !== activeId);
      const insertTasks = [...(overGroup.tasks || [])].filter((t) => t.id !== activeId);
      insertTasks.splice(newIndex, 0, draggedTask);
      overGroup.tasks = insertTasks;
    },
    [filteredTasksList, currentProject?.tasks, findContainer]
  );

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveTaskId(null);
      setSourceContainerId(null);

      if (disableDragAndDrop || !over || !filteredTasksList) return;
      if (
        taskFilter?.groupingStatus === SHOW_TASK_GROUPING_TYPE.NO_GROUPING ||
        taskFilter?.groupingStatus === SHOW_TASK_GROUPING_TYPE.GROUPING_BY_USER
      ) {
        return;
      }

      const activeId = String(active.id);
      const overId = String(over.id);
      const overContainer = findContainer(overId);
      if (!overContainer) return;

      const draggedTask = currentProject?.tasks?.find((task) => task.id === activeId);
      if (!draggedTask) return;

      const sourceGroupId = sourceContainerId || overContainer;
      const destinationGroupId = overContainer;

      const destinationGroup = filteredTasksList.find((g) => String(g.id) === destinationGroupId);
      if (!destinationGroup) return;

      const destinationTasks = (destinationGroup.tasks || []).filter((t) => t.id !== activeId);
      const finalIndex = over.data.current?.sortable?.index ?? 0;

      if (sourceGroupId === destinationGroupId) {
        if (taskFilter?.orderByStatus !== SHOW_TASK_BY_ORDER.CUSTOM) return;

        const sourceGroup = filteredTasksList.find((g) => String(g.id) === sourceGroupId);
        if (!sourceGroup) return;

        const groupTasks = sourceGroup.tasks || [];
        const activeIndex = groupTasks.findIndex((t) => t.id === activeId);
        const overIdx = groupTasks.findIndex((t) => t.id === overId);
        if (activeIndex === overIdx) return;

        let updatedTaskIndex: number;
        if (overIdx === 0) {
          const nextTaskIndex = groupTasks[0]?.index;
          updatedTaskIndex = nextTaskIndex ? nextTaskIndex - 50 : draggedTask.createdOn.getTime();
        } else if (overIdx === groupTasks.length - 1) {
          const previousTaskIndex = groupTasks[groupTasks.length - 1]?.index;
          updatedTaskIndex = previousTaskIndex ? previousTaskIndex + 50 : draggedTask.createdOn.getTime();
        } else {
          if (overIdx > activeIndex) {
            const previousTaskIndex: number = groupTasks[overIdx]?.index || 0;
            const nextTaskIndex: number = groupTasks[overIdx + 1]?.index || 0;
            updatedTaskIndex = (previousTaskIndex + nextTaskIndex) / 2;
          } else {
            const previousTaskIndex: number = groupTasks[overIdx - 1]?.index || 0;
            const nextTaskIndex: number = groupTasks[overIdx]?.index || 0;
            updatedTaskIndex = (previousTaskIndex + nextTaskIndex) / 2;
          }
        }
        draggedTask.setIndex(updatedTaskIndex);
        sourceGroup.tasks = arrayMove(groupTasks, activeIndex, overIdx);
        const payloadTask = { index: updatedTaskIndex, projectId: currentProject?.id };
        await updateTaskDetails(activeId, payloadTask);
        return;
      }

      // Calculate custom order index for cross-group drag
      const getCustomOrderIndex = () => {
        if (taskFilter?.orderByStatus !== SHOW_TASK_BY_ORDER.CUSTOM) return undefined;
        if (destinationTasks.length === 0 || finalIndex > destinationTasks.length - 1) {
          return destinationTasks.length === 0
            ? draggedTask.createdOn.getTime()
            : (destinationTasks[destinationTasks.length - 1]?.index || 0) + 50;
        } else if (finalIndex === 0) {
          const nextTaskIndex = destinationTasks[0]?.index;
          return nextTaskIndex ? nextTaskIndex - 50 : draggedTask.createdOn.getTime();
        } else {
          const previousTaskIndex = destinationTasks[finalIndex - 1]?.index || 0;
          const nextTaskIndex = destinationTasks[finalIndex]?.index || 0;
          return (previousTaskIndex + nextTaskIndex) / 2;
        }
      };

      // Cross-group drag
      let payload: {
        index?: number;
        groupId?: string;
        statusId?: string;
        milestoneId?: string;
        priority?: number;
      } = {};
      if (
        taskFilter?.groupingStatus === SHOW_TASK_GROUPING_TYPE.GROUPING_BY_GROUP ||
        (taskFilter?.groupingStatus === SHOW_TASK_GROUPING_TYPE.DEFAULT && currentProject?.isGroupEnabled)
      ) {
        const newTaskIndex = getCustomOrderIndex();
        if (newTaskIndex !== undefined) {
          draggedTask.setIndex(newTaskIndex);
          payload.index = newTaskIndex;
        }
        draggedTask.setGroup(destinationGroupId);
        const taskGroups = draggedTask.project?.taskGroups;
        const previousGroup = taskGroups?.find((x) => x?.id === sourceGroupId);
        const updatedGroup = taskGroups?.find((x) => x?.id === destinationGroupId);
        createNewComment(activeId, {
          Type: COMMENT_TYPE.CHANGE_GROUP,
          PreviousGroup: previousGroup?.name,
          Group: updatedGroup?.name
        });
        payload.groupId = destinationGroupId;
      } else if (taskFilter?.groupingStatus === SHOW_TASK_GROUPING_TYPE.GROUPING_BY_STATUS) {
        const newTaskIndex = getCustomOrderIndex();
        if (newTaskIndex !== undefined) {
          draggedTask.setIndex(newTaskIndex);
          payload.index = newTaskIndex;
        }
        draggedTask.setStatus(destinationGroupId);
        const currentStatus = currentProject?.statusData?.find((x) => x['_id'] === destinationGroupId);
        const prevStatus = currentProject?.statusData?.find((x) => x['_id'] === sourceGroupId);
        createNewComment(activeId, {
          Type: COMMENT_TYPE.STATUS_ID_CHANGE,
          PreviousGroup: prevStatus?.Name,
          Group: currentStatus?.Name,
          Priority: currentStatus?.Type
        });
        payload.statusId = destinationGroupId;
      } else if (taskFilter?.groupingStatus === SHOW_TASK_GROUPING_TYPE.GROUPING_BY_MILESTONE) {
        draggedTask.setMilestone(destinationGroupId);
        const milestoneList = draggedTask?.project?.milestones || [];
        const milestone = milestoneList?.find((x) => x?.id === destinationGroupId);
        createNewComment(activeId, {
          Type: COMMENT_TYPE.SET_MILESTONE,
          Group: milestone?.milestoneName
        });
        payload = { milestoneId: destinationGroupId };
      } else if (taskFilter?.groupingStatus === SHOW_TASK_GROUPING_TYPE.GROUPING_BY_PRIORITY) {
        draggedTask.setPriority(Number(destinationGroupId));
        createNewComment(activeId, {
          Type: COMMENT_TYPE.CHANGE_PRIORITY,
          PreviousPriority: Number(sourceGroupId) === 5 ? TASK_PRIORITY.NO_PRIORITY : Number(sourceGroupId),
          Priority: Number(destinationGroupId) === 5 ? TASK_PRIORITY.NO_PRIORITY : Number(destinationGroupId)
        });
        payload = {
          priority: draggedTask.priority === 5 ? TASK_PRIORITY.NO_PRIORITY : Number(destinationGroupId)
        };
      }

      const payloadTask = { ...(payload || {}), projectId: currentProject?.id };
      const resultUpdate = await updateTaskDetails(activeId, payloadTask);
      if (resultUpdate) {
        trackAnalyticActivity(TASK_ANALYTICS.GROUP_CHANGED);
      }
    },
    [
      currentProject?.id,
      currentProject?.isGroupEnabled,
      currentProject?.statusData,
      currentProject?.tasks,
      disableDragAndDrop,
      filteredTasksList,
      findContainer,
      sourceContainerId,
      taskFilter?.groupingStatus,
      taskFilter?.orderByStatus
    ]
  );

  //handle delete task
  const deleteTask = useCallback(async (task: TaskModel) => {
    try {
      setDeleteLoading(true);
      const payloadTask = { isArchived: true, projectId: task?.projectId };
      const response = await updateTaskDetails(task?.id, payloadTask);
      setIsModalOpen(false);
      setSelectedItem(undefined);
      if (response) {
        rootStore.taskStore.removeTasks([task?.id]);
      }
    } catch (error) {
      captureException(error);
    } finally {
      setDeleteLoading(false);
    }
  }, []);

  useHotkeys('c', async () => {
    setTimeout(async () => {
      setOpen(true);
      rootStore.taskStore.createTaskInput.setProject(currentProject?.id || '');
    }, 100);
  });

  //handle task milestone
  const updateTaskMilestone = useCallback(
    async (currentTask: TaskModel, milestone: Milestone) => {
      currentTask.setMilestone(milestone?.id);
      const payloadTask = { milestoneId: milestone?.id, projectId: currentTask?.projectId };
      const result = await updateTaskDetails(currentTask?.id, payloadTask);
      const commentResult = await createNewComment(currentTask?.id, {
        Type: COMMENT_TYPE.SET_MILESTONE,
        Group: milestone?.milestoneName
      });
      if (result && commentResult && loadData) {
        loadData();
      }
    },
    [loadData]
  );

  //handle remove milestone
  const onRemoveMilestone = useCallback(async (task: TaskModel) => {
    if (!isEmpty(task?.milestoneId)) {
      task.setMilestone('No Milestone');
      const payloadTask = { milestoneIdUnset: true, projectId: task?.projectId };
      await updateTaskDetails(task?.id, payloadTask);
    }
  }, []);

  const handleCollapseClick = useCallback((id: string) => {
    const groupId = String(id);
    setCollapseIds((prev) =>
      prev.includes(groupId) ? prev.filter((existingGroupId) => existingGroupId !== groupId) : [...prev, groupId]
    );
  }, []);

  const renderTaskCardContent = useCallback(
    (task: TaskModel) => (
      <MyCustomContextMenuV2
        taskData={task}
        loadData={loadData}
        currentProject={task?.project!}
        onClickView={onClickTaskDetails}
        onClickClone={onClickClone}
        onClickEdit={onClickEdit}
        statusList={task?.project?.statusData || EMPTY_STATUS_LIST}
        onClickDelete={OpenDeleteModal}
        onUpdateStatus={onChangeStatus}
        isView={isView}>
        <BlockContent id={task?.id} key={task?.id}>
          <Calendarviewcard
            taskDetails={task}
            currentProject={task?.project!}
            isTaskGrid={true}
            loadData={loadData}
            onClickCurrentTaskCheckbox={onClickTaskCheckBox}
            onUpdateStatus={onChangeStatus}
            onUpdateMilestone={updateTaskMilestone}
            onRemoveMilestone={onRemoveMilestone}
            isView={isView}
          />
        </BlockContent>
      </MyCustomContextMenuV2>
    ),
    [
      OpenDeleteModal,
      isView,
      loadData,
      onChangeStatus,
      onClickClone,
      onClickEdit,
      onClickTaskCheckBox,
      onClickTaskDetails,
      onRemoveMilestone,
      updateTaskMilestone
    ]
  );

  // render completed task data
  const renderDisplayCompletedTaskData = useCallback(
    (item: MobxFilteredTasksListInterface, comparedValue: string) => {
      return (
        selectedId === comparedValue &&
        showCompletedTask &&
        item?.completedTasks &&
        item?.completedTasks?.length > 0 && (
          <>
            {item?.completedTasks?.map((task, index) => {
              if (item?.isCollapse) return <></>;
              const isLast = index === (item?.completedTasks?.length || 0) - 1;
              return (
                <TaskContainer key={task?.id} isLastChild={isLast}>
                  {renderTaskCardContent(task)}
                </TaskContainer>
              );
            })}
          </>
        )
      );
    },
    [renderTaskCardContent, selectedId, showCompletedTask]
  );

  // render completed task data
  const renderCompletedTaskData = useCallback(
    (item: MobxFilteredTasksListInterface, comparedValue: string) => {
      return (
        item?.completedTasks &&
        item?.completedTasks?.length > 0 && (
          <CompletedTasksToggle
            comparedValue={comparedValue}
            selectedId={selectedId}
            showCompletedTask={showCompletedTask}
            toggleCompletedTask={toggleCompletedTask}>
            {renderDisplayCompletedTaskData(item, comparedValue)}
          </CompletedTasksToggle>
        )
      );
    },
    [renderDisplayCompletedTaskData, selectedId, showCompletedTask, toggleCompletedTask]
  );

  // get compared value
  const getComparedValue = useCallback(
    (item: MobxFilteredTasksListInterface) => {
      let comparedValue;
      if (
        groupingStatus === SHOW_TASK_GROUPING_TYPE.GROUPING_BY_USER ||
        groupingStatus === SHOW_TASK_GROUPING_TYPE.GROUPING_BY_PRIORITY
      ) {
        comparedValue = item?.name;
      } else {
        comparedValue = item?.id || '';
      }
      return comparedValue.toString();
    },
    [groupingStatus]
  );

  const renderFilterTaskList = useMemo(() => {
    return filteredTasksList?.map((item) => {
      const comparedValue = getComparedValue(item);
      const isCollapsed = collapseIds.includes(String(item.id));
      return (
        <DroppableGroup
          key={item.id}
          item={item}
          comparedValue={comparedValue}
          isCollapsed={isCollapsed}
          groupingStatus={groupingStatus}
          disableDragAndDrop={disableDragAndDrop}
          renderTaskCardContent={renderTaskCardContent}
          renderCompletedTaskData={renderCompletedTaskData}
          loadData={loadData}
          workspace={workspace}
          collapseIds={collapseIds}
          handleCollapseClick={handleCollapseClick}
        />
      );
    });
  }, [
    collapseIds,
    disableDragAndDrop,
    filteredTasksList,
    getComparedValue,
    groupingStatus,
    handleCollapseClick,
    loadData,
    renderCompletedTaskData,
    renderTaskCardContent,
    workspace
  ]);

  // render other task data
  const renderOtherTask = useMemo(() => {
    return otherTask?.tasks?.map((item) => {
      return <TaskContainer key={item?.id}>{renderTaskCardContent(item)}</TaskContainer>;
    });
  }, [otherTask?.tasks, renderTaskCardContent]);

  // render other completed task
  const renderOtherCompletedTask = useMemo(() => {
    return (
      otherTask?.completedTasks &&
      otherTask?.completedTasks?.length > 0 && (
        <CompletedTasksToggle
          comparedValue='otherTasks'
          selectedId={selectedId}
          showCompletedTask={showCompletedTask}
          toggleCompletedTask={toggleCompletedTask}>
          {selectedId === 'otherTasks' && showCompletedTask && otherTask?.completedTasks?.length > 0 && (
            <>
              {otherTask?.completedTasks?.map((item, index) => {
                const isLast = index === (otherTask?.completedTasks?.length || 0) - 1;
                return (
                  <TaskContainer key={item?.id} isLastChild={isLast}>
                    {renderTaskCardContent(item)}
                  </TaskContainer>
                );
              })}
            </>
          )}
        </CompletedTasksToggle>
      )
    );
  }, [otherTask?.completedTasks, renderTaskCardContent, selectedId, showCompletedTask, toggleCompletedTask]);

  //render all task data
  const renderTaskData = useMemo(() => {
    if (
      filteredTasksList?.length > 0 ||
      otherTask?.tasks?.length > 0 ||
      (otherTask?.completedTasks && otherTask?.completedTasks?.length > 0)
    ) {
      return (
        <>
          {renderFilterTaskList}
          {!loading &&
            (otherTask?.tasks?.length > 0 || (otherTask?.completedTasks && otherTask?.completedTasks?.length > 0)) && (
              <BoxModel>
                <Header isHide={groupingStatus === SHOW_TASK_GROUPING_TYPE.NO_GROUPING}>
                  <Title>Other Tasks</Title>
                </Header>
                <Div>
                  {renderOtherTask}
                  {renderOtherCompletedTask}
                </Div>
              </BoxModel>
            )}
        </>
      );
    }
  }, [
    filteredTasksList?.length,
    otherTask?.tasks?.length,
    otherTask?.completedTasks,
    renderFilterTaskList,
    groupingStatus,
    renderOtherTask,
    renderOtherCompletedTask,
    loading
  ]);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}>
      <MainContent className='desktop-main-content-grid-view'>
        {loading && filteredTasksList?.length === 0 ? <Taskgridloading /> : <Container>{renderTaskData}</Container>}
        <Modal open={isModalOpen} onClose={closeDeleteModal} maxWidth={'334px'}>
          <DeleteModal
            onClose={closeDeleteModal}
            onOk={() => selectedItem && deleteTask(selectedItem)}
            loading={deleteLoading}
            modalTitle='Delete Task?'
            description='Are you sure you want to delete this task?'
          />
        </Modal>
        <Modal open={open} onClose={close} isCreateTask={true} maxWidth={'680px'}>
          <CreateTaskModalV2 loadData={loadData} onCancel={close} context={{ source: 'shortcut' }} />
        </Modal>
        {confirmationModal}
        <DragOverlay>
          {activeTask ? <BlockContent>{renderTaskCardContent(activeTask)}</BlockContent> : null}
        </DragOverlay>
      </MainContent>
    </DndContext>
  );
};

export default observer(TaskGrid);
